import i18n from "@/plugins/i18n.plugin";

/*
type TStep = {
  title: string;
  orderId: number;
}

type TSteps = TStep[];

type TFailedItem = {
  isShowIcon: boolean;
  desc: string;
  items: string[];
};

type TFailedItems = TFailedItem[];
*/

function defaultPopupProgress() {
    return {
        progressBar: {
            isShow: true,
            value: 0,
            total: 100,
        },
        stepData: {
            currentStep: 1,
            failedSteps: [], // number[]
            steps: [], // TSteps
        },
        reminder: "",
    };
}

function defaultPopupResult() {
    return {
        message: "",
        buttons: {
            ok: {
                label: "",
                isShow: true,
            },
            cancel: {
                label: "",
                isShow: false,
            },
        },
        failedItems: [], // TFailedItems
    };
}

function defaultSteps(isUpload, isSubmit) {
    const steps = [i18n.t("lbl_EMPBE_SubmitAlert_SavingToSystem")];

    if (isUpload) {
        steps.unshift(i18n.t("lbl_EMPBE_SubmitAlert_UploadingAllFiles"));
    }

    if (isSubmit) {
        steps.push(i18n.t("lbl_EMPBE_SubmitAlert_SubmitingForm"));
    }

    return steps.map((step, index) => ({ title: step, orderId: index + 1 }));
}

function defaultTitleAndReminder() {
    return {
        header: i18n.t("lbl_EMPBE_SubmitAlert_SubmittingDataAndFilesTitle"),
        reminder: i18n.t("lbl_EMPBE_SubmitAlert_SavingLoadingReminder"),
    };
}

function defaultResultHeader(isSuccess, isSubmit) {
    const submitTitle = isSuccess
        ? i18n.t("lbl_EMPBE_SubmitAlert_SubmitSuccessTitle")
        : i18n.t("lbl_EMPBE_SubmitAlert_SubmitFailedTitle");
    const saveTitle = isSuccess
        ? i18n.t("lbl_EMPBE_SubmitAlert_SaveSuccessTitle")
        : i18n.t("lbl_EMPBE_SubmitAlert_SaveFailedTitle");

    return isSubmit ? submitTitle : saveTitle;
}

function defaultResultMessage(isSuccess, isSubmit) {
    const submitMessage = isSuccess
        ? i18n.t("lbl_EMPBE_SubmitAlert_SubmitSuccessMessage")
        : i18n.t("lbl_EMPBE_SubmitAlert_SubmitFailedMsg");
    const saveMessage = isSuccess
        ? i18n.t("lbl_EMPBE_SubmitAlert_SaveSuccessMessage")
        : i18n.t("lbl_EMPBE_SubmitAlert_SaveFailedMsg");

    return isSubmit ? submitMessage : saveMessage;
}

const initialState = {
    isShowModal: false,
    header: "",
    status: "progress", // "progress" | "result"
    progress: defaultPopupProgress(),
    result: defaultPopupResult(),
};

const getters = {};

const mutations = {
    // Reset
    ResetMessageModal(state) {
        state.isShowModal = initialState.isShowModal;
        state.header = initialState.header;
        state.status = initialState.status;
        state.progress = defaultPopupProgress();
        state.result = defaultPopupResult();
    },

    // Set isShowModal
    SetIsShowModal(state, isShowModal) {
        state.isShowModal = isShowModal;
    },

    // Set Header
    SetHeader(state, header) {
        state.header = header;
    },

    // Set Status
    SetStatus(state, status) {
        state.status = status;
    },

    // Update Progress Bar
    UpdateProgressBar(state, progressBarOptions) {
        const currProgressBar = { ...state.progress.progressBar };

        state.progress.progressBar = {
            ...currProgressBar,
            ...progressBarOptions,
        };
    },

    // Update Step Data
    UpdateStepData(state, stepDataOptions) {
        const currStepData = { ...state.progress.stepData };

        state.progress.stepData = {
            ...currStepData,
            ...stepDataOptions,
        };
    },

    // Update Reminder
    UpdateReminder(state, reminder) {
        state.progress.reminder = reminder;
    },

    // Increment Step No
    IncrementStep(state) {
        state.progress.stepData.currentStep += 1;
    },

    // Add Failed Step
    AddFailedStep(state, orderId) {
        state.progress.stepData.failedSteps.push(orderId);
    },

    // Update Result
    UpdateResult(state, result) {
        const currResult = { ...state.result };

        state.result = {
            ...currResult,
            ...result,
        };
    },
};

const actions = {
    /* ----------- Basic Function Start ----------- */
    // Hide
    HideMessageModal({ commit }) {
        commit("SetIsShowModal", false);
        commit("ResetMessageModal");
    },

    // Show
    ShowMessageModal({ commit }, { header, status }) {
        commit("SetStatus", status);
        commit("SetHeader", header);
        commit("SetIsShowModal", true);
    },

    // Show Progress
    ShowProgressMessage({ commit, dispatch }, { header = "", progressBarOptions, stepDataOptions, reminder = "" }) {
        if (progressBarOptions) {
            commit("UpdateProgressBar", progressBarOptions);
        }

        if (stepDataOptions) {
            commit("UpdateStepData", stepDataOptions);
        }

        if (!!reminder) {
            commit("UpdateReminder", reminder);
        }

        dispatch("ShowMessageModal", { header, status: "progress" });
    },

    // Next Step
    NextStep({ state, commit }, { isCurrentStepFailed = false } = {}) {
        const currentStep = state.progress.stepData.currentStep;

        commit("IncrementStep");

        if (isCurrentStepFailed) {
            commit("AddFailedStep", currentStep);
        }
    },

    // Show Result
    ShowResultMessage({ commit, dispatch }, { header = "", result = {} }) {
        commit("UpdateResult", result);

        dispatch("ShowMessageModal", { header, status: "result" });
    },

    // Update Upload Progress (Assume Progress Bar is already shown)
    UpdateUploadProgress({ commit }, { noOfUploadedFile }) {
        commit("UpdateProgressBar", { value: noOfUploadedFile });
    },
    /* ----------- Basic Function End ----------- */

    /* ----------- Complex Function Start ----------- */
    // Show Progress With Upload Progress Bar or Update Upload Progress
    ShowProgressWithUpload({ commit, dispatch }, { total, isSubmit = false }) {
        const steps = defaultSteps(true, isSubmit);

        dispatch("ShowProgressMessage", {
            ...defaultTitleAndReminder(),
            progressBarOptions: {
                value: 0, // Just start uploading, 0 is uploaded
                total,
                isShow: true,
            },
            stepDataOptions: {
                steps,
                currentStep: 1,
                failedSteps: [],
            },
        });
    },

    // Show Progress Without Upload Progress Bar Message popup
    ShowProgressWithoutUpload({ dispatch }, { isSubmit = false }) {
        const steps = defaultSteps(false, isSubmit);

        dispatch("ShowProgressMessage", {
            ...defaultTitleAndReminder(),
            progressBarOptions: { isShow: false },
            stepDataOptions: {
                steps,
                currentStep: 1,
                failedSteps: [],
            },
        });
    },

    FinishUpload({ dispatch, commit }, { isSuccess }) {
        commit("UpdateProgressBar", { isShow: false });
        dispatch("NextStep", { isCurrentStepFailed: !isSuccess });
    },

    ShowResult(
        { dispatch },
        { isSuccess, isSubmit = false, message = "", header = "", uploadFailedItems = [], missingFields = [] },
    ) {
        const failedItems = [];
        const isUploadFailed = uploadFailedItems && uploadFailedItems.length > 0;
        if (isUploadFailed) {
            failedItems.push({
                isShowIcon: true,
                desc: i18n.t("lbl_EMPBE_SubmitAlert_UploadFailedItemsMsg"),
                items: uploadFailedItems,
            });
        }
        const isMissingFields = missingFields && missingFields.length > 0;
        if (isMissingFields) {
            failedItems.push({
                isShowIcon: true,
                desc: i18n.t("lbl_EMPBE_SubmitAlert_MissingMandatoryItemsMsg"),
                items: missingFields,
            });
        }

        const resultHeader = header || defaultResultHeader(isSuccess, isSubmit);
        if (!message || isUploadFailed || isMissingFields) {
            message = defaultResultMessage(isSuccess && !isUploadFailed && !isMissingFields, isSubmit);
        }

        dispatch("ShowResultMessage", {
            header: resultHeader,
            result: {
                message,
                failedItems,
            },
        });
    },
    /* ----------- Complex Function End ----------- */
};

export default {
    namespaced: true,
    state: initialState,
    getters,
    actions,
    mutations,
};
